import { Delegate } from './delegate.js';

class MegaMenuLevels extends HTMLElement {
  #delegate = null;
  #scrollObserver = null;
  #openObserver = null;
  #details = null;
  #megaMenu = null;
  #header = null;
  #onMenuShown = null;
  #onResize = null;

  connectedCallback() {
    this.#delegate = new Delegate(this);
    this.#delegate.on('click', '[data-action="open-mega-panel"]', this.#onTriggerClick);
    this.#delegate.on('click', '[data-action="mega-menu-scroll"]', this.#onScrollButtonClick);
    this.#delegate.on('keypress', '[data-action="mega-menu-scroll"]', this.#onScrollButtonKeypress);
    this.#delegate.on('pointerdown', '[data-action="mega-menu-scrollbar"]', this.#onThumbPointerDown);
    this.#delegate.on('keydown', '[data-action="mega-menu-scrollbar"]', this.#onThumbKeydown);
    this.#delegate.on('scroll', '[data-mega-scroll]', this.#onScroll, { passive: true, capture: true });

    const scrollAreas = this.querySelectorAll('[data-mega-scroll-area]');
    this.#scrollObserver = new ResizeObserver(() => this.updateScrollStates());
    scrollAreas.forEach((area) => {
      const scrollEl = area.querySelector('[data-mega-scroll]');
      if (scrollEl) this.#scrollObserver.observe(scrollEl);

      const content = area.querySelector('.mega-menu__list--secondary, .mega-menu__panels');
      if (content) this.#scrollObserver.observe(content);
    });

    this.#details = this.closest('details[is="details-mega"]');
    this.#megaMenu = this.closest('.mega-menu');
    this.#header = this.closest('header');
    this.#onMenuShown = () => {
      this.syncInlineStart();
      this.syncAvailableHeight();
      this.updateScrollStates();
    };
    this.#details?.addEventListener('menu:handleAfterShow', this.#onMenuShown);

    this.#onResize = () => {
      this.syncInlineStart();
      this.syncAvailableHeight();
    };
    window.addEventListener('resize', this.#onResize, { passive: true });

    this.#openObserver = new MutationObserver(() => {
      if (this.#details?.hasAttribute('open') || this.#megaMenu?.hasAttribute('open')) {
        this.syncInlineStart();
        this.syncAvailableHeight();
        this.scheduleScrollStateUpdate();
      } else {
        // Fully closed: the next open starts both columns from the top.
        this.querySelectorAll('[data-mega-scroll]').forEach((scrollEl) => scrollEl.scrollTo({ top: 0, behavior: 'instant' }));
      }
    });
    if (this.#details) {
      this.#openObserver.observe(this.#details, { attributes: true, attributeFilter: ['open'] });
    }
    if (this.#megaMenu) {
      this.#openObserver.observe(this.#megaMenu, { attributes: true, attributeFilter: ['open'] });
    }

    this.syncInlineStart();
    // Measure up front: if the variable is still unset on the first open, the menu
    // paints at the `100vh` fallback height and then snaps to the real one.
    this.syncAvailableHeight();
    this.scheduleScrollStateUpdate();
  }

  disconnectedCallback() {
    this.#delegate?.destroy();
    this.#delegate = null;
    this.#details?.removeEventListener('menu:handleAfterShow', this.#onMenuShown);
    this.#onMenuShown = null;
    window.removeEventListener('resize', this.#onResize);
    this.#onResize = null;
    this.#scrollObserver?.disconnect();
    this.#scrollObserver = null;
    this.#openObserver?.disconnect();
    this.#openObserver = null;
    this.#details = null;
    this.#megaMenu = null;
    this.#header = null;
  }

  /** Match the centered top nav track so columns start under the first menu item. */
  syncInlineStart() {
    const menu = this.#header?.querySelector('.header__navigation .header__menu');
    if (!menu || !this.#header) return;

    const width = Math.round(menu.getBoundingClientRect().width);
    if (width <= 0) return;

    const firstLink = menu.querySelector('ul > li .menu__item, ul > li summary');
    const itemPad = firstLink
      ? Math.round(parseFloat(getComputedStyle(firstLink).paddingInlineStart) || 0)
      : 16;

    this.#header.style.setProperty('--header-menu-width', `${width}px`);
    this.#header.style.setProperty('--mega-menu-item-pad', `${itemPad}px`);
  }

  /** Keep the menu inside the viewport on short screens; page scroll is locked while it is open. */
  syncAvailableHeight() {
    if (!this.#header) return;

    const available = Math.round(window.innerHeight - this.#header.getBoundingClientRect().bottom);
    this.style.setProperty('--mega-menu-available-height', `${available}px`);
  }

  scheduleScrollStateUpdate() {
    requestAnimationFrame(() => this.updateScrollStates());
  }

  #onTriggerClick = (event, trigger) => {
    const targetId = trigger.getAttribute('data-toggle-id');
    if (!targetId) return;

    const item = trigger.closest('.mega-menu__item');
    // Already open → allow the link to navigate
    if (item?.classList.contains('is-active') || trigger.getAttribute('aria-expanded') === 'true') {
      const activePanel = this.querySelector(`#${CSS.escape(targetId)}`);
      if (activePanel && !activePanel.hasAttribute('hidden')) return;
    }

    event.preventDefault();
    this.activate(trigger);
  };

  #onScroll = (event, scrollEl) => {
    const area = scrollEl.closest('[data-mega-scroll-area]');
    if (area) this.#updateScrollState(area);
  };

  /** "Scroll for more" jumps to the bottom; at the bottom it reads "Back to top" and jumps up. */
  #onScrollButtonClick = (event, button) => {
    const area = button.closest('[data-mega-scroll-area]');
    const scrollEl = area?.querySelector('[data-mega-scroll]');
    if (!scrollEl) return;

    const top = area.classList.contains('is-scrolled-end') ? 0 : scrollEl.scrollHeight;
    scrollEl.scrollTo({ top, behavior: 'smooth' });
  };

  /** Mirrors click activation for Enter keypresses on the scroll button. */
  #onScrollButtonKeypress = (event, button) => {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    this.#onScrollButtonClick(event, button);
  };

  /** Drag the custom scrollbar thumb like a native one. */
  #onThumbPointerDown = (event, thumb) => {
    const scrollEl = document.getElementById(thumb.getAttribute('aria-controls'));
    if (!scrollEl) return;

    event.preventDefault();
    const startY = event.clientY;
    const startTop = scrollEl.scrollTop;
    const ratio = scrollEl.scrollHeight / thumb.parentElement.clientHeight;

    const onMove = (moveEvent) => {
      scrollEl.scrollTo({ top: startTop + (moveEvent.clientY - startY) * ratio, behavior: 'instant' });
    };
    // Direct listeners on purpose: they only live for one drag, and pointer capture sends every
    // move to the thumb even when the pointer leaves it. Use Delegate for regular events.
    const onUp = () => {
      thumb.removeEventListener('pointermove', onMove);
      thumb.removeEventListener('pointerup', onUp);
      thumb.removeEventListener('pointercancel', onUp);
    };

    thumb.setPointerCapture(event.pointerId);
    thumb.addEventListener('pointermove', onMove);
    thumb.addEventListener('pointerup', onUp);
    thumb.addEventListener('pointercancel', onUp);
  };

  /** Keyboard control for the scrollbar thumb: arrows, Page Up/Down, Home/End. */
  #onThumbKeydown = (event, thumb) => {
    const scrollEl = document.getElementById(thumb.getAttribute('aria-controls'));
    if (!scrollEl) return;

    const page = scrollEl.clientHeight * 0.9;
    const steps = { ArrowUp: -40, ArrowDown: 40, PageUp: -page, PageDown: page };
    let top;
    if (event.key in steps) top = scrollEl.scrollTop + steps[event.key];
    else if (event.key === 'Home') top = 0;
    else if (event.key === 'End') top = scrollEl.scrollHeight;
    else return;

    event.preventDefault();
    scrollEl.scrollTo({ top, behavior: 'smooth' });
  };

  #updateScrollState(area) {
    const scrollEl = area.querySelector('[data-mega-scroll]');
    if (!scrollEl) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollEl;
    const isScrollable = scrollHeight > clientHeight + 1;
    const atEnd = !isScrollable || scrollTop + clientHeight >= scrollHeight - 1;
    area.classList.toggle('is-scrollable', isScrollable);
    area.classList.toggle('is-scrolled-end', atEnd);

    // Only the visible label should be announced.
    area.querySelector('.mega-menu__scroll-btn-label--more')?.setAttribute('aria-hidden', String(atEnd));
    area.querySelector('.mega-menu__scroll-btn-label--top')?.setAttribute('aria-hidden', String(!atEnd));

    const scrollbar = this.querySelector(`[data-mega-scrollbar="${area.dataset.megaScrollArea}"]`);
    if (!scrollbar) return;

    scrollbar.classList.toggle('is-scrollable', isScrollable);
    scrollbar.style.setProperty('--thumb-height', `${(clientHeight / scrollHeight) * 100}%`);
    scrollbar.style.setProperty('--thumb-top', `${(scrollTop / scrollHeight) * 100}%`);

    const maxScroll = scrollHeight - clientHeight;
    const progress = maxScroll > 0 ? Math.round((scrollTop / maxScroll) * 100) : 0;
    scrollbar.querySelector('[data-action="mega-menu-scrollbar"]')?.setAttribute('aria-valuenow', String(progress));
  }

  updateScrollStates() {
    this.querySelectorAll('[data-mega-scroll-area]').forEach((area) => this.#updateScrollState(area));
  }

  activate(trigger) {
    const targetId = trigger.getAttribute('data-toggle-id');

    this.querySelectorAll('[data-action="open-mega-panel"]').forEach((el) => {
      const isActive = el === trigger;
      el.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      el.closest('.mega-menu__item')?.classList.toggle('is-active', isActive);
    });

    this.querySelectorAll('[data-mega-panel]').forEach((panel) => {
      const isActive = Boolean(targetId) && panel.id === targetId;
      panel.classList.toggle('is-active', isActive);
      panel.toggleAttribute('hidden', !isActive);
      panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    const panelsScroll = this.querySelector('.mega-menu__scroll-area--panels [data-mega-scroll]');
    if (panelsScroll) panelsScroll.scrollTop = 0;

    requestAnimationFrame(() => this.scheduleScrollStateUpdate());
  }
}

if (!customElements.get('mega-menu-levels')) {
  customElements.define('mega-menu-levels', MegaMenuLevels);
}
